import React, { useCallback, useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

// Supported file types
const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];

const SUPPORTED_EXTENSIONS = ['.pdf', '.docx'];

// Maximum file size: 50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

export type UploadStatus = 'idle' | 'validating' | 'uploading' | 'success' | 'error';

export interface UploadedFile {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
}

export interface DocumentUploadProps {
  onUpload?: (file: File) => Promise<{ success: boolean; documentId?: string; error?: string }>;
  onFileSelect?: (file: File) => void;
  maxFileSize?: number;
  disabled?: boolean;
  className?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const validateFile = (file: File, maxSize: number): { valid: boolean; error?: string } => {
  // Check file type by MIME type
  const isValidMimeType = SUPPORTED_MIME_TYPES.includes(file.type);

  // Also check by extension as a fallback
  const fileName = file.name.toLowerCase();
  const isValidExtension = SUPPORTED_EXTENSIONS.some(ext => fileName.endsWith(ext));

  if (!isValidMimeType && !isValidExtension) {
    return {
      valid: false,
      error: `Invalid file type. Supported formats: ${SUPPORTED_EXTENSIONS.join(', ')}`,
    };
  }

  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds limit of ${formatFileSize(maxSize)}`,
    };
  }

  // Check for empty file
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty',
    };
  }

  return { valid: true };
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUpload,
  onFileSelect,
  maxFileSize = MAX_FILE_SIZE,
  disabled = false,
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const processFile = useCallback(async (file: File) => {
    // Create initial upload state
    const uploadFile: UploadedFile = {
      id: crypto.randomUUID(),
      file,
      status: 'validating',
      progress: 0,
    };
    setUploadedFile(uploadFile);

    // Validate file
    const validation = validateFile(file, maxFileSize);
    if (!validation.valid) {
      setUploadedFile({
        ...uploadFile,
        status: 'error',
        error: validation.error,
      });
      return;
    }

    // Notify parent of file selection
    if (onFileSelect) {
      onFileSelect(file);
    }

    // If upload handler provided, perform upload
    if (onUpload) {
      setUploadedFile(prev => prev ? { ...prev, status: 'uploading', progress: 0 } : null);

      // Simulate progress (in production, you'd track actual upload progress)
      const progressInterval = setInterval(() => {
        setUploadedFile(prev => {
          if (!prev || prev.status !== 'uploading') {
            clearInterval(progressInterval);
            return prev;
          }
          const newProgress = Math.min(prev.progress + 10, 90);
          return { ...prev, progress: newProgress };
        });
      }, 200);

      try {
        const result = await onUpload(file);
        clearInterval(progressInterval);

        if (result.success) {
          setUploadedFile(prev => prev ? {
            ...prev,
            status: 'success',
            progress: 100,
          } : null);
        } else {
          setUploadedFile(prev => prev ? {
            ...prev,
            status: 'error',
            error: result.error || 'Upload failed',
          } : null);
        }
      } catch (error) {
        clearInterval(progressInterval);
        setUploadedFile(prev => prev ? {
          ...prev,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        } : null);
      }
    } else {
      // No upload handler - just mark as ready
      setUploadedFile({
        ...uploadFile,
        status: 'success',
        progress: 100,
      });
    }
  }, [maxFileSize, onUpload, onFileSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, [disabled, processFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [disabled, processFile]);

  const handleBrowseClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  const handleRetry = useCallback(() => {
    if (uploadedFile) {
      processFile(uploadedFile.file);
    }
  }, [uploadedFile, processFile]);

  const renderUploadZone = () => (
    <div
      className={`
        upload-zone
        ${isDragging ? 'upload-zone-active' : ''}
        ${disabled ? 'upload-zone-disabled' : ''}
      `}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleBrowseClick}
      data-testid="upload-zone"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleBrowseClick();
        }
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={SUPPORTED_EXTENSIONS.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="file-input"
        disabled={disabled}
      />

      <div className="flex flex-col items-center justify-center">
        <div
          className={`
            p-4 rounded-full mb-4 transition-colors duration-200
            ${isDragging
              ? 'bg-primary-100 text-primary-600'
              : 'bg-neutral-100 text-neutral-400 group-hover:bg-neutral-200'
            }
          `}
        >
          <Upload className="h-10 w-10" />
        </div>

        <p className="upload-title" data-testid="upload-title">
          {isDragging ? 'Drop your file here' : 'Drag and drop your contract document'}
        </p>
        <p className="upload-subtitle" data-testid="upload-subtitle">
          or{' '}
          <span className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
            browse files
          </span>
        </p>

        <div className="upload-hint" data-testid="upload-hints">
          <span>PDF or DOCX</span>
          <span className="upload-hint-dot" />
          <span>Max {formatFileSize(maxFileSize)}</span>
        </div>
      </div>
    </div>
  );

  const renderFileStatus = () => {
    if (!uploadedFile) return null;

    const { file, status, progress, error } = uploadedFile;

    return (
      <div
        className={`
          upload-file-preview mt-4
          ${status === 'error' ? 'border-danger-200 bg-danger-50' : ''}
          ${status === 'success' ? 'border-success-200 bg-success-50' : ''}
          ${status === 'uploading' || status === 'validating' ? 'border-primary-200 bg-primary-50' : ''}
        `}
        data-testid="file-status"
      >
        <div className="upload-file-info">
          <div
            className={`
              upload-file-icon
              ${status === 'error' ? 'bg-danger-100 text-danger-600' : ''}
              ${status === 'success' ? 'bg-success-100 text-success-600' : ''}
              ${status === 'uploading' || status === 'validating' ? 'bg-primary-100 text-primary-600' : ''}
            `}
          >
            {status === 'error' && <AlertCircle className="h-5 w-5" />}
            {status === 'success' && <CheckCircle className="h-5 w-5" />}
            {(status === 'uploading' || status === 'validating') && (
              <Loader2 className="h-5 w-5 animate-spin" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <p
                className="upload-file-name"
                data-testid="file-name"
                title={file.name}
              >
                {file.name}
              </p>
            </div>
            <p className="upload-file-size" data-testid="file-size">
              {formatFileSize(file.size)}
            </p>

            {/* Progress bar for uploading status */}
            {status === 'uploading' && (
              <div className="upload-progress">
                <div className="flex items-center justify-between text-xs text-neutral-500 mb-1">
                  <span>Uploading...</span>
                  <span data-testid="upload-progress">{progress}%</span>
                </div>
                <div className="upload-progress-bar">
                  <div
                    className="upload-progress-fill"
                    style={{ width: `${progress}%` }}
                    data-testid="progress-bar"
                  />
                </div>
              </div>
            )}

            {/* Validating status */}
            {status === 'validating' && (
              <p className="input-helper text-primary-600" data-testid="validating-message">
                Validating file...
              </p>
            )}

            {/* Success message */}
            {status === 'success' && (
              <p className="input-success-message" data-testid="success-message">
                <CheckCircle className="h-3 w-3" />
                File uploaded successfully
              </p>
            )}

            {/* Error message */}
            {status === 'error' && error && (
              <p className="input-error-message" data-testid="error-message">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* Retry button for errors */}
          {status === 'error' && (
            <button
              onClick={handleRetry}
              className="btn-ghost-primary btn-xs"
              data-testid="retry-button"
            >
              Retry
            </button>
          )}

          {/* Remove button */}
          <button
            onClick={handleRemoveFile}
            className="btn-icon-sm btn-ghost text-neutral-400 hover:text-neutral-600"
            data-testid="remove-file-button"
            title="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={className} data-testid="document-upload">
      {renderUploadZone()}
      {renderFileStatus()}
    </div>
  );
};

export default DocumentUpload;
